/**
 * Asset Automation Service
 * Automatically fetches assets from the internet based on training module analysis
 * Supports: 3D models (Sketchfab), Images (Unsplash/Pexels), Videos (Pexels), Audio (Freesound/Pixabay)
 */

import { sketchfabService } from '../../../utils/automation/sketchfabService';
import { ollamaService } from './ollamaService';
import toast from 'react-hot-toast';
import store from '../../../redux/store';
import {
  add_new_assets,
  addModel,
  addImage,
  addVideo,
  addAudio,
  addSceneMesh,
  addSceneImage,
  addSceneVideo,
  addSceneAudio,
} from '../../../redux/Slice/gridSlice';

// Production: limits and retries
const MAX_ASSETS_PER_AUTOFETCH = 20;
const ASSET_FETCH_RETRIES = 2;
const ASSET_FETCH_RETRY_DELAY_MS = 1000;

// Keywords that indicate the primary entity/character type and take search priority over position words like "house owner"
const PRIMARY_ENTITY_KEYWORDS = new Set([
  'avatar', 'person', 'character', 'human', 'man', 'woman', 'boy', 'girl',
  'worker', 'operator', 'robot', 'creature', 'npc', 'soldier', 'guard',
  'car', 'truck', 'bus', 'bike', 'motorcycle', 'vehicle',
  'aircraft', 'plane', 'helicopter', 'ship', 'boat',
  'animal', 'cat', 'dog', 'horse', 'bird',
  'tree', 'plant', 'flower',
]);

// Semantic synonyms to add when searching for character/entity types
const CHARACTER_SEARCH_SYNONYMS = {
  avatar: ['avatar', 'human character', 'person 3d', 'character model'],
  person: ['person', 'human', 'character 3d'],
  character: ['character', 'human character', '3d character'],
  human: ['human', 'person', 'character'],
  worker: ['worker character', 'worker', 'human worker'],
  operator: ['operator character', 'operator', 'human character'],
  robot: ['robot', 'robot character', 'android 3d'],
  creature: ['creature', 'monster character'],
  soldier: ['soldier character', 'soldier 3d'],
};

// Words that indicate architecture/building results (for mismatch penalizing)
const ARCHITECTURE_RESULT_WORDS = ['house', 'building', 'home', 'residence', 'architecture', 'villa', 'mansion'];

// Map training asset categories (from LLM) to Sketchfab API category slugs for more exact search
const SKETCHFAB_CATEGORY_MAP = {
  safety_equipment: 'science-technology',
  wall_mounted: 'electronics-gadgets',
  floor_equipment: 'architecture',
  handheld_tool: 'electronics-gadgets',
  workbench_item: 'furniture-home',
  material: 'art-abstract',
};
// Minimum relevance score to accept a result; below this we try fallback or report "no good match"
const SKETCHFAB_MIN_RELEVANCE_SCORE = 15;

const LOCAL_LIBRARY_URL = 'https://server.spatialgrid.ai/api/v1/library';

export class AssetAutomationService {
  constructor() {
    this.unsplashAccessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY || '';
    this.pexelsApiKey = process.env.REACT_APP_PEXELS_API_KEY || '';
    this.pixabayApiKey = process.env.REACT_APP_PIXABAY_API_KEY || '';
    this.freesoundApiKey = process.env.REACT_APP_FREESOUND_API_KEY || '';
    this.baseUrl = process.env.REACT_APP_BASE_URL;
  }

  /**
   * Search the local asset library.
   * Does NOT force isPublic=true — authenticated users should see their own assets.
   */
  async searchLocalLibrary(query, { limit = 12, type = '3D' } = {}) {
    const token = localStorage.getItem('token') || '';
    const params = new URLSearchParams({ type, limit: String(limit) });
    if (query && query.trim()) params.set('search', query.trim());
    const url = `${LOCAL_LIBRARY_URL}/?${params}`;
    console.log('[Library] GET', url);
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Library search failed: ${res.status}`);
    const data = await res.json();
    const raw = data.data?.items || data.data || data.items || data.results || [];
    const rawArr = Array.isArray(raw) ? raw : [];

    // API wraps results: each element is { subtype, data: [...actual items] }
    // Flatten by expanding item.data when it is an array
    const normalized = [];
    for (const item of rawArr) {
      if (item && Array.isArray(item.data)) {
        // data is an array of real asset objects → expand each one
        for (const asset of item.data) {
          normalized.push({ ...asset, subtype: item.subtype });
        }
      } else if (item && item.data && typeof item.data === 'object') {
        normalized.push({ ...item.data, subtype: item.subtype });
      } else {
        normalized.push(item);
      }
    }

    console.log(`[Library] query="${query}" → ${normalized.length} items`);
    if (normalized.length > 0) {
      console.log('[Library] unwrapped ALL keys:', Object.keys(normalized[0]));
      console.log('[Library] unwrapped full item:', JSON.stringify(normalized[0]).slice(0, 400));
    }
    return normalized;
  }

  /**
   * Search local library with multiple query variants so partial-name assets
   * (e.g. library has "terrace", search is "Roof Top Terrace") are not missed.
   * Falls back to fetching all library items + client-side filtering when
   * every search query returns zero results.
   */
  async _searchLibraryWithVariants(assetName, { limit = 12, type = '3D' } = {}, description = '') {
    const keyWords = this.extractKeyWords(assetName);
    const mainWord = this.getMainSearchWord(assetName);
    const mainNoun = this.extractMainNoun(assetName);
    const descKeyWords = description ? this.extractKeyWords(description).slice(0, 3) : [];

    const queries = [assetName, mainWord, mainNoun, ...keyWords, ...descKeyWords]
      .filter(Boolean)
      .map(q => q.toLowerCase().trim());
    const uniqueQueries = [...new Set(queries)].slice(0, 8);

    console.log('[Library] variant queries:', uniqueQueries);

    const seenKeys = new Set();
    const allItems = [];

    // Robust dedup key: try every common ID field, fall back to name
    const itemKey = (item) =>
      String(item._id || item.id || item.uid || item.assetId || item.name || Math.random());

    await Promise.allSettled(
      uniqueQueries.map(async (q) => {
        try {
          const items = await this.searchLocalLibrary(q, { limit, type });
          for (const item of items) {
            const key = itemKey(item);
            if (!seenKeys.has(key)) { seenKeys.add(key); allItems.push(item); }
          }
        } catch (_) {}
      })
    );

    // Fallback: fetch ALL library items and match client-side
    if (allItems.length === 0) {
      console.log('[Library] search returned nothing — fetching all items for client-side match');
      try {
        const allRaw = await this.searchLocalLibrary('', { limit: 100, type });
        console.log('[Library] all items count:', allRaw.length);
        if (allRaw.length > 0) console.log('[Library] sample item keys:', Object.keys(allRaw[0]));
        const searchWords = [...new Set([...queries])].filter(w => w.length > 2);
        for (const item of allRaw) {
          const key = itemKey(item);
          if (seenKeys.has(key)) continue;
          const haystack = `${item.name || ''} ${item.description || ''} ${(item.tags || []).join(' ')}`.toLowerCase();
          const matches = searchWords.some(w => haystack.includes(w));
          if (matches) { seenKeys.add(key); allItems.push(item); }
        }
        console.log('[Library] client-side match found', allItems.length, 'items');
      } catch (_) {}
    }

    return allItems;
  }

  /** Map a local library item to the uniform candidate shape used throughout the picker. */
  _libraryItemToCandidate(item) {
    const BACKEND = LOCAL_LIBRARY_URL.replace('/api/v1/library', ''); // e.g. https://server.spatialgrid.ai
    const resolveUrl = (url) => {
      if (!url) return null;
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      return `${BACKEND}${url.startsWith('/') ? '' : '/'}${url}`;
    };
    const rawThumb = item.thumbnail || item.thumbnail_url || item.preview
      || item.image || item.imageUrl || item.image_url
      || item.coverImage || item.cover_image || item.previewImage
      || item.preview_url || item.previewUrl || item.cover
      || item.thumbUrl || item.thumb || item.filePreview || item.assetPreview || null;
    const uid = item._id || item.id || item.uid || item.assetId || item.name;
    const thumbnail = resolveUrl(rawThumb);
    // Prefer streamUrl (Draco-compressed + WebP textures) over the raw original
    const fileUrl = resolveUrl(item.streamUrl || item.stream_url
      || item.file_url || item.fileUrl || item.url || item.glb_url
      || item.modelUrl || item.model_url || item.assetUrl || item.asset_url || item.file || '');
    return {
      uid,
      name: item.name || 'Untitled',
      author: 'Local Library',
      thumbnail,
      isDownloadable: true,
      source: 'library',
      fileUrl,
      description: item.description || '',
      tags: Array.isArray(item.tags) ? item.tags : [],
    };
  }

  /** Download an asset from the local library and return a File + metadata. */
  async downloadFromLibrary(candidate, assetName) {
    const fileUrl = candidate.fileUrl;
    if (!fileUrl) throw new Error('Local library item has no file URL');
    // Don't send Authorization to external CDN URLs — it triggers a CORS preflight
    // that CloudFront doesn't handle. CloudFront files are already public.
    const isExternal = /^https?:\/\/(?!localhost)/.test(fileUrl);
    const token = localStorage.getItem('token') || '';
    const res = await fetch(fileUrl, {
      headers: isExternal ? {} : (token ? { Authorization: `Bearer ${token}` } : {}),
    });
    if (!res.ok) throw new Error(`Library download failed: ${res.status}`);
    const arrayBuffer = await res.arrayBuffer();
    const safeName = (assetName || candidate.name).replace(/\s+/g, '_');
    const fileName = `${safeName}_${candidate.uid}.glb`;
    const file = new File([arrayBuffer], fileName, { type: 'model/gltf-binary', lastModified: Date.now() });
    return {
      success: true, file, fileName, assetType: 'mesh', source: 'library',
      metadata: { name: candidate.name, author: 'Local Library', libraryId: candidate.uid, thumbnail: candidate.thumbnail },
    };
  }

  /** Retry an async fetch fn (e.g. fetchMeshAsset) with backoff */
  async _fetchWithRetry(fn, retries = ASSET_FETCH_RETRIES) {
    let lastResult;
    for (let attempt = 0; attempt <= retries; attempt++) {
      lastResult = await fn();
      if (lastResult?.success) return lastResult;
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, ASSET_FETCH_RETRY_DELAY_MS * Math.pow(2, attempt)));
      }
    }
    return lastResult;
  }

  /**
   * Get auth token from localStorage
   */
  getAuthToken() {
    return localStorage.getItem('token') || '';
  }

  /**
   * Get auth headers
   */
  getAuthHeaders() {
    return {
      Authorization: `Bearer ${this.getAuthToken()}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Build enhanced search query from asset information
   */
  buildSearchQuery(assetName, description = '', usage = '') {
    // Start with the asset name
    let query = assetName.trim();

    // Add key terms from description if available
    if (description) {
      // Extract important keywords (remove common words)
      const stopWords = new Set([
        'the',
        'a',
        'an',
        'and',
        'or',
        'but',
        'in',
        'on',
        'at',
        'to',
        'for',
        'of',
        'with',
        'is',
        'are',
        'was',
        'were',
      ]);
      const descWords = description
        .toLowerCase()
        .split(/\s+/)
        .filter((word) => word.length > 3 && !stopWords.has(word))
        .slice(0, 3); // Take top 3 keywords

      if (descWords.length > 0) {
        query += ' ' + descWords.join(' ');
      }
    }

    // Add usage context if it provides additional context
    if (usage && usage.length < 50) {
      const stopWords = new Set([
        'the',
        'a',
        'an',
        'and',
        'or',
        'but',
        'in',
        'on',
        'at',
        'to',
        'for',
        'of',
        'with',
        'is',
        'are',
        'was',
        'were',
      ]);
      const usageWords = usage
        .toLowerCase()
        .split(/\s+/)
        .filter((word) => word.length > 4 && !stopWords.has(word))
        .slice(0, 2);

      if (usageWords.length > 0) {
        query += ' ' + usageWords.join(' ');
      }
    }

    return query.trim();
  }

  /**
   * Extract key words from a phrase, removing adjectives and keeping nouns
   */
  extractKeyWords(text) {
    const stopWords = new Set([
      'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with',
      'is', 'are', 'was', 'were', 'this', 'that', 'these', 'those', 'it', 'its',
    ]);

    // Common adjectives/descriptors to remove (can be expanded)
    const adjectives = new Set([
      'indian', 'american', 'european', 'asian', 'african', 'large', 'small', 'big', 'tiny',
      'modern', 'traditional', 'vintage', 'classic', 'new', 'old', 'ancient', 'contemporary',
      'exterior', 'interior', 'outdoor', 'indoor', 'residential', 'commercial', 'industrial',
      'wooden', 'metal', 'plastic', 'glass', 'stone', 'brick', 'concrete', 'white', 'black',
      'red', 'blue', 'green', 'yellow', 'brown', 'gray', 'grey', 'orange', 'pink', 'purple',
    ]);

    const words = text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ') // Remove special characters
      .split(/\s+/)
      .filter((word) => {
        const cleanWord = word.trim();
        return (
          cleanWord.length > 2 &&
          !stopWords.has(cleanWord) &&
          !adjectives.has(cleanWord)
        );
      });

    return words;
  }

  /**
   * Extract the main noun from a phrase (usually the last significant word)
   * For "Indian Residential House Exterior" -> "house"
   */
  extractMainNoun(text) {
    const words = text.toLowerCase().split(/\s+/);
    const adjectives = new Set([
      'indian', 'american', 'european', 'asian', 'african', 'residential', 'commercial',
      'industrial', 'exterior', 'interior', 'outdoor', 'indoor', 'modern', 'traditional',
      'vintage', 'classic', 'large', 'small', 'big', 'tiny', 'new', 'old', 'ancient',
    ]);

    // Look for the main noun (usually appears after adjectives)
    // Common nouns that are likely the main subject
    const commonNouns = new Set([
      'house', 'building', 'home', 'residence', 'structure', 'model', 'object', 'item',
      'valve', 'pump', 'motor', 'engine', 'machine', 'equipment', 'tool', 'device',
      'button', 'switch', 'lever', 'handle', 'knob', 'dial', 'control', 'panel',
      'door', 'window', 'wall', 'floor', 'ceiling', 'roof', 'fence', 'gate',
      'table', 'chair', 'desk', 'cabinet', 'shelf', 'drawer', 'box', 'container',
    ]);

    // Try to find the main noun (usually the last non-adjective word, or a known common noun)
    for (let i = words.length - 1; i >= 0; i--) {
      const word = words[i].replace(/[^a-z0-9]/g, '');
      if (word.length > 2 && !adjectives.has(word)) {
        // If it's a known common noun, return it
        if (commonNouns.has(word)) {
          return word;
        }
        // Otherwise, return the last significant word
        if (i === words.length - 1 || i === words.length - 2) {
          return word;
        }
      }
    }

    // Fallback: return the last word if nothing else found
    const lastWord = words[words.length - 1]?.replace(/[^a-z0-9]/g, '');
    return lastWord && lastWord.length > 2 ? lastWord : null;
  }

  /**
   * Generate multiple query variants for better search success rate
   * Returns 5-7 different query variations, including simplified versions
   */
  generateQueryVariants(assetName, description = '', usage = '') {
    const variants = [];
    const cleanName = assetName.trim();
    const words = cleanName.split(/\s+/).filter(w => w.trim().length > 0);
    const keyWords = this.extractKeyWords(cleanName);

    // Variant 1: Enhanced query with description and usage (most specific)
    const enhancedQuery = this.buildSearchQuery(assetName, description, usage);
    if (enhancedQuery && enhancedQuery !== cleanName) {
      variants.push(enhancedQuery);
    }

    // Variant 2: Just the asset name (exact match)
    variants.push(cleanName);

    // Variant 2b: Keyword-style searches (e.g. "bahubali statue" → "bahubali", "bahubali statue", "statue")
    // So we try full phrase and each word so Sketchfab finds results when one variant has few hits
    if (words.length >= 1) {
      const genericWords = new Set(['statue', 'model', 'figure', 'object', 'item', '3d']);
      words.forEach((w) => {
        const word = w.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (word.length >= 2 && !genericWords.has(word)) {
          if (!variants.includes(word)) variants.push(word);
          variants.push(`${word} 3d`);
        }
      });
      if (words.length >= 2) {
        const twoWordPhrase = words.slice(0, 2).join(' ');
        if (!variants.includes(twoWordPhrase)) variants.push(twoWordPhrase);
        variants.push(`${twoWordPhrase} 3d`);
      }
    }

    // Variant 3: Asset name with "3d" suffix
    variants.push(`${cleanName} 3d`);

    // Variant 4: Asset name with "3d model" (more specific)
    variants.push(`${cleanName} 3d model`);

    // Variant 5: Simplified name (remove special chars, lowercase)
    const simplified = cleanName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (simplified && simplified !== cleanName.toLowerCase()) {
      variants.push(simplified);
    }

    // Variant 6: Key words only (main nouns, remove adjectives)
    if (keyWords.length > 0) {
      const keyWordsQuery = keyWords.join(' ');
      if (keyWordsQuery !== simplified) {
        variants.push(keyWordsQuery);
        // Also try with "3d"
        variants.push(`${keyWordsQuery} 3d`);
      }
    }

    // Variant 7: Last 2 words (often the most important part)
    if (words.length > 2) {
      const lastTwoWords = words.slice(-2).join(' ');
      if (!variants.includes(lastTwoWords)) {
        variants.push(lastTwoWords);
        variants.push(`${lastTwoWords} 3d`);
      }
    }

    // Variant 8: Extract main noun (core subject) - e.g., "house" from "Indian Residential House Exterior"
    const mainNoun = this.extractMainNoun(cleanName);
    if (mainNoun && mainNoun.length > 2) {
      if (!variants.includes(mainNoun)) {
        variants.push(mainNoun);
        variants.push(`${mainNoun} 3d`);
      }

      // Also try main noun with last word if it's different (e.g., "house exterior")
      if (words.length > 1) {
        const lastWord = words[words.length - 1].toLowerCase().replace(/[^a-z0-9]/g, '');
        if (lastWord !== mainNoun && lastWord.length > 2) {
          const adjectives = new Set(['exterior', 'interior', 'outdoor', 'indoor']);
          if (!adjectives.has(lastWord) || words.length > 2) {
            const nounWithModifier = `${mainNoun} ${lastWord}`;
            if (!variants.includes(nounWithModifier)) {
              variants.push(nounWithModifier);
              variants.push(`${nounWithModifier} 3d`);
            }
          }
        }
      }

      // If the main noun is a character/entity type, prepend semantic synonyms at the front
      // so Sketchfab sees the most targeted queries first
      if (CHARACTER_SEARCH_SYNONYMS[mainNoun]) {
        CHARACTER_SEARCH_SYNONYMS[mainNoun].forEach((sv) => {
          if (!variants.includes(sv)) variants.unshift(sv);
        });
      }
    }

    // Variant 9: First significant word (skip adjectives)
    if (words.length > 1) {
      const adjectives = new Set([
        'indian', 'american', 'european', 'asian', 'residential', 'commercial', 'industrial',
        'exterior', 'interior', 'outdoor', 'indoor', 'modern', 'traditional',
      ]);

      const significantWords = words.filter(w => {
        const lower = w.toLowerCase().replace(/[^a-z0-9]/g, '');
        return lower.length > 2 && !adjectives.has(lower);
      });

      if (significantWords.length > 0) {
        const firstSignificant = significantWords[0].toLowerCase().replace(/[^a-z0-9]/g, '');
        if (firstSignificant.length > 3 && firstSignificant !== mainNoun && !variants.includes(firstSignificant)) {
          variants.push(firstSignificant);
          variants.push(`${firstSignificant} 3d`);
        }
      }
    }

    // Variant 9: Extract key words from description if available
    if (description) {
      const descKeyWords = this.extractKeyWords(description).slice(0, 2);
      if (descKeyWords.length > 0) {
        const descQuery = `${keyWords.length > 0 ? keyWords.slice(0, 2).join(' ') : cleanName} ${descKeyWords.join(' ')}`;
        if (!variants.includes(descQuery)) {
          variants.push(descQuery);
        }
      }
    }

    // Remove duplicates and empty strings, prioritize longer/more specific queries
    const uniqueVariants = [...new Set(variants.filter(v => v && v.trim().length > 0))];

    // Sort by length (longer = more specific); return up to 10 so keyword-style terms (e.g. "bahubali", "bahubali statue") are all tried
    const sortedVariants = uniqueVariants.sort((a, b) => b.length - a.length);

    return sortedVariants.slice(0, 10);
  }

  /**
   * Score search result relevance (name, description, usage, tags)
   */
  scoreRelevance(result, assetName, description = '', usage = '') {
    let score = 0;
    const resultName = (result.name || '').toLowerCase();
    const resultDesc = (result.description || '').toLowerCase();
    const assetNameLower = assetName.toLowerCase();
    const descLower = description.toLowerCase();
    const usageLower = (usage || '').toLowerCase();
    const resultTags = (result.tags || []).map((t) => (typeof t === 'string' ? t : t.name || '').toLowerCase());

    // Exact name match (highest score)
    if (resultName === assetNameLower) {
      score += 100;
    } else if (
      resultName.includes(assetNameLower) ||
      assetNameLower.includes(resultName)
    ) {
      score += 50;
    }

    // Normalize for partial match: remove punctuation, collapse spaces
    const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
    const assetNorm = norm(assetName);
    const resultNorm = norm(resultName);
    if (assetNorm && resultNorm && (resultNorm.includes(assetNorm) || assetNorm.includes(resultNorm))) {
      score += 30;
    }

    // Word overlap with asset name
    const assetWords = assetNameLower.split(/\s+/).filter((w) => w.length > 1);
    const resultWords = resultName.split(/\s+/);
    const commonWords = assetWords.filter((word) => resultWords.some((rw) => rw.includes(word) || word.includes(rw)));
    score += commonWords.length * 12;

    // Strong boost: the result's entire name is one of the search keywords
    // e.g. library asset "terrace" searched by "Roof Top Terrace" → high relevance
    const resultNameNorm = resultName.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (resultNameNorm.length > 3 && assetWords.includes(resultNameNorm)) {
      score += 25;
    }

    // Tag match: if Sketchfab result has tags that match asset name words, boost score
    if (resultTags.length > 0 && assetWords.length > 0) {
      const tagMatchCount = assetWords.filter((aw) =>
        resultTags.some((tag) => tag.includes(aw) || aw.includes(tag))
      ).length;
      score += tagMatchCount * 15;
    }

    // Description relevance: search description words vs result description words
    if (description && resultDesc) {
      const descWords = descLower.split(/\s+/).filter((w) => w.length > 3);
      const resultDescWords = new Set(
        resultDesc.split(/\s+/).filter((w) => w.length > 3)
      );
      const commonDescWords = descWords.filter((w) => resultDescWords.has(w));
      score += commonDescWords.length * 5;
    }

    // Asset name words vs result description — library assets have rich descriptions
    // e.g. searching "Roof Top Terrace" should match a library item whose description
    // mentions "terrace", "rooftop", "residential" etc.
    if (resultDesc && assetWords.length > 0) {
      const resultDescTokens = resultDesc
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((w) => w.length > 3);
      const resultDescSet = new Set(resultDescTokens);
      const nameWordsInDesc = assetWords.filter((w) => w.length > 3 && resultDescSet.has(w));
      score += nameWordsInDesc.length * 8;
    }

    // Usage relevance (e.g. "used in assembly step 2")
    if (usageLower && resultDesc) {
      const usageWords = usageLower.split(/\s+/).filter((w) => w.length > 3);
      const matchCount = usageWords.filter((w) => resultDesc.includes(w) || resultName.includes(w)).length;
      score += matchCount * 3;
    }

    // Quality indicators (prefer well-used, liked models when relevance is similar)
    if (result.downloadCount) {
      score += Math.min(result.downloadCount / 100, 10);
    }
    if (result.likeCount) {
      score += Math.min(result.likeCount / 50, 5);
    }

    // Type-mismatch penalty: if searching for a character/person but result is architecture
    const searchingForCharacter = assetNameLower.split(/\s+/).some((w) => CHARACTER_SEARCH_SYNONYMS[w] !== undefined)
      || ['avatar', 'person', 'character', 'human', 'worker', 'operator'].some((w) => assetNameLower.includes(w));
    if (searchingForCharacter) {
      const resultIsArchitecture = ARCHITECTURE_RESULT_WORDS.some((w) => resultName.includes(w));
      if (resultIsArchitecture) score -= 60;
    }

    return score;
  }

  /**
   * Get the main/distinctive word from an asset name for search (e.g. "bahubali" from "bahubali statue").
   * Skips generic words like "statue", "model", "figure" so we always search with the specific term.
   */
  getMainSearchWord(assetName) {
    if (!assetName || typeof assetName !== 'string') return '';
    const genericWords = new Set([
      'statue', 'model', 'figure', 'object', 'item', '3d', 'mesh', 'asset',
      'piece', 'sculpture', 'image', 'video', 'audio', 'icon', 'element',
    ]);
    const words = assetName.trim().split(/\s+/).filter((w) => w.length > 0);

    // Scan right-to-left: entity-type keywords (avatar, person, robot…) take priority
    // over positional/role words like "house owner" in "house owner avatar"
    for (let i = words.length - 1; i >= 0; i--) {
      const clean = words[i].toLowerCase().replace(/[^a-z0-9]/g, '');
      if (PRIMARY_ENTITY_KEYWORDS.has(clean)) return clean;
    }

    for (const w of words) {
      const clean = w.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (clean.length >= 2 && !genericWords.has(clean)) {
        return clean;
      }
    }
    return words[0] ? words[0].toLowerCase().replace(/[^a-z0-9]/g, '') : '';
  }

  /**
   * Search and download 3D models from Sketchfab
   * Uses query variants, optional Sketchfab categories/tags, and relevance scoring for more exact matches.
   */
  async fetchMeshAsset(assetName, options = {}) {
    try {
      const {
        maxResults = 24,
        description = '',
        usage = '',
        category = '',
        tags: assetTags = '',
      } = options;

      // Map training category to Sketchfab category slug(s) for narrower search
      const sketchfabCategories = category && SKETCHFAB_CATEGORY_MAP[category]
        ? SKETCHFAB_CATEGORY_MAP[category]
        : '';

      // Optional tags for Sketchfab (e.g. "industrial", "valve") - comma-separated
      const sketchfabTags = typeof assetTags === 'string'
        ? assetTags
        : Array.isArray(assetTags)
          ? assetTags.slice(0, 3).join(',')
          : '';

      const searchOptions = {
        count: maxResults,
        downloadable: true,
        sort_by: 'relevance',
        ...(sketchfabCategories && { categories: sketchfabCategories }),
        ...(sketchfabTags && { tags: sketchfabTags }),
      };

      // Prefer AI-suggested keywords for Sketchfab (e.g. "bahubali statue" → ["bahubali", "bahubali statue", ...])
      let queryVariants = [];
      try {
        const aiKeywords = await ollamaService.getSketchfabSearchKeywords(
          assetName,
          description,
          usage
        );
        if (aiKeywords && aiKeywords.length > 0) {
          queryVariants = [...aiKeywords];
          // Add " 3d" variant for each keyword to improve Sketchfab results
          aiKeywords.forEach((k) => {
            const with3d = `${k.trim()} 3d`;
            if (!queryVariants.includes(with3d)) queryVariants.push(with3d);
          });
          queryVariants = queryVariants.slice(0, 10);
        }
      } catch (e) {
        console.warn('AI Sketchfab keywords failed, using fallback variants:', e?.message);
      }
      if (queryVariants.length === 0) {
        queryVariants = this.generateQueryVariants(assetName, description, usage);
      }

      // Always search with the main word of the name first (e.g. "bahubali" for "bahubali statue")
      const mainWord = this.getMainSearchWord(assetName);
      if (mainWord) {
        const mainTerm3d = `${mainWord} 3d`;
        const rest = queryVariants.filter((q) => q !== mainWord && q !== mainTerm3d);
        queryVariants = [mainWord, mainTerm3d, ...rest];
      }

      // ── 1. Check local library first (multi-query so partial names like "terrace" are found) ──
      try {
        const libraryItems = await this._searchLibraryWithVariants(assetName, { limit: 12 }, description);
        if (libraryItems.length > 0) {
          const scored = libraryItems
            .map(item => ({
              ...this._libraryItemToCandidate(item),
              relevanceScore: this.scoreRelevance(
                { name: item.name, description: item.description || '', tags: item.tags },
                assetName, description, usage
              ),
            }))
            .sort((a, b) => b.relevanceScore - a.relevanceScore);
          if (scored[0].relevanceScore >= SKETCHFAB_MIN_RELEVANCE_SCORE) {
            return await this.downloadFromLibrary(scored[0], assetName);
          }
        }
      } catch (libErr) {
        console.warn('Local library search failed, falling back to Sketchfab:', libErr.message);
      }
      // ── 2. Fall back to Sketchfab ──────────────────────────────────────────

      const allResults = [];
      const seenUids = new Set();

      for (let i = 0; i < queryVariants.length; i++) {
        const query = queryVariants[i];
        try {
          const searchResults = await sketchfabService.searchModels(query, searchOptions);

          if (searchResults && searchResults.length > 0) {
            for (const model of searchResults) {
              if (model.isDownloadable !== false && !seenUids.has(model.uid)) {
                seenUids.add(model.uid);
                allResults.push(model);
              }
            }
            if (allResults.length >= maxResults * 2) {
              break;
            }
          }
        } catch (variantError) {
          console.warn(`Sketchfab query "${query}" failed:`, variantError.message);
          continue;
        }
      }

      if (allResults.length === 0) {
        throw new Error(
          `No downloadable 3D models found for "${assetName}" after trying ${queryVariants.length} query variants. Try a different name or add a more specific description.`
        );
      }

      const scoredResults = allResults
        .filter((model) => model.isDownloadable !== false)
        .map((model) => ({
          ...model,
          relevanceScore: this.scoreRelevance(
            model,
            assetName,
            description,
            usage
          ),
        }))
        .sort((a, b) => b.relevanceScore - a.relevanceScore);

      if (scoredResults.length === 0) {
        throw new Error(`No downloadable models found for "${assetName}"`);
      }

      let selectedModel = scoredResults[0];

      // If best score is below threshold, try search with sort_by 'popular' for a safer default
      if (selectedModel.relevanceScore < SKETCHFAB_MIN_RELEVANCE_SCORE && queryVariants.length > 0) {
        try {
          const fallbackResults = await sketchfabService.searchModels(queryVariants[0], {
            ...searchOptions,
            sort_by: 'popular',
            count: maxResults,
          });
          if (fallbackResults && fallbackResults.length > 0) {
            const fallbackScored = fallbackResults
              .filter((m) => m.isDownloadable !== false)
              .map((m) => ({
                ...m,
                relevanceScore: this.scoreRelevance(m, assetName, description, usage),
              }))
              .sort((a, b) => b.relevanceScore - a.relevanceScore);
            if (fallbackScored[0].relevanceScore > selectedModel.relevanceScore) {
              selectedModel = fallbackScored[0];
            }
          }
        } catch (e) {
          // Keep original selection
        }
      }


      // Get download URL
      const downloadUrl = await sketchfabService.getModelDownloadUrl(
        selectedModel.uid
      );

      // Download the file
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to download model: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const fileName = `${assetName.replace(/\s+/g, '_')}_${selectedModel.uid}.glb`;
      const file = new File([arrayBuffer], fileName, {
        type: 'model/gltf-binary',
        lastModified: Date.now(),
      });

      return {
        success: true,
        file,
        fileName,
        assetType: 'mesh',
        source: 'sketchfab',
        metadata: {
          name: selectedModel.name,
          author: selectedModel.author,
          license: selectedModel.license,
          sketchfabUid: selectedModel.uid,
          downloadUrl,
          relevanceScore: selectedModel.relevanceScore,
          queryVariantsTried: queryVariants.length,
          totalResultsFound: allResults.length,
          alternatives: scoredResults.slice(1, 4).map((r) => ({
            name: r.name,
            uid: r.uid,
            score: r.relevanceScore,
          })),
        },
      };
    } catch (error) {
      console.error('Mesh asset fetch error:', error);
      return {
        success: false,
        error: error.message,
        assetType: 'mesh',
      };
    }
  }

  /**
   * Search Sketchfab and return scored candidates WITHOUT downloading.
   * Call downloadMeshCandidate() once the user picks one.
   */
  async searchMeshCandidates(assetName, options = {}) {
    const { maxResults = 24, description = '', usage = '', category = '', tags: assetTags = '' } = options;
    const sketchfabCategories = category && SKETCHFAB_CATEGORY_MAP[category] ? SKETCHFAB_CATEGORY_MAP[category] : '';
    const sketchfabTags = typeof assetTags === 'string' ? assetTags : Array.isArray(assetTags) ? assetTags.slice(0, 3).join(',') : '';
    const searchOptions = {
      count: maxResults, downloadable: true, sort_by: 'relevance',
      ...(sketchfabCategories && { categories: sketchfabCategories }),
      ...(sketchfabTags && { tags: sketchfabTags }),
    };

    // ── 1. Local library candidates (multi-query so partial names are found) ──
    let libraryCandidates = [];
    try {
      const libraryItems = await this._searchLibraryWithVariants(assetName, { limit: 12 }, description);
      libraryCandidates = libraryItems
        .map(item => ({
          ...this._libraryItemToCandidate(item),
          relevanceScore: this.scoreRelevance(
            { name: item.name, description: item.description || '', tags: item.tags },
            assetName, description, usage
          ),
        }))
        .sort((a, b) => b.relevanceScore - a.relevanceScore);
    } catch (e) {
      console.warn('Library search failed, using Sketchfab only:', e.message);
    }

    // ── 2. Sketchfab candidates ─────────────────────────────────────────────
    let queryVariants = [];
    try {
      const aiKeywords = await ollamaService.getSketchfabSearchKeywords(assetName, description, usage);
      if (aiKeywords && aiKeywords.length > 0) {
        queryVariants = [...aiKeywords];
        aiKeywords.forEach((k) => { const w = `${k.trim()} 3d`; if (!queryVariants.includes(w)) queryVariants.push(w); });
        queryVariants = queryVariants.slice(0, 10);
      }
    } catch (e) {}
    if (queryVariants.length === 0) queryVariants = this.generateQueryVariants(assetName, description, usage);
    const mainWord = this.getMainSearchWord(assetName);
    if (mainWord) { const t = `${mainWord} 3d`; queryVariants = [mainWord, t, ...queryVariants.filter(q => q !== mainWord && q !== t)]; }

    const allResults = []; const seenUids = new Set();
    for (const query of queryVariants) {
      try {
        const results = await sketchfabService.searchModels(query, searchOptions);
        if (results && results.length > 0) {
          for (const m of results) { if (m.isDownloadable !== false && !seenUids.has(m.uid)) { seenUids.add(m.uid); allResults.push(m); } }
          if (allResults.length >= maxResults * 2) break;
        }
      } catch (e) { continue; }
    }

    const sketchfabCandidates = allResults
      .filter(m => m.isDownloadable !== false)
      .map(m => ({ ...m, source: 'sketchfab', relevanceScore: this.scoreRelevance(m, assetName, description, usage) }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, 12);

    if (libraryCandidates.length === 0 && sketchfabCandidates.length === 0) {
      return { success: false, error: `No models found for "${assetName}"`, candidates: [] };
    }

    // Local library results appear first in the picker
    const seenFinal = new Set();
    const candidates = [...libraryCandidates, ...sketchfabCandidates]
      .filter(c => { if (seenFinal.has(c.uid)) return false; seenFinal.add(c.uid); return true; })
      .slice(0, 16);

    return { success: true, candidates };
  }

  /** Search only the local library and return scored candidates. */
  async searchLibraryCandidates(assetName, options = {}) {
    const { description = '', usage = '', limit = 12 } = options;
    try {
      // Use multi-variant search so partial-name assets (e.g. "terrace" for "Rooftop Terrace") are found
      const items = await this._searchLibraryWithVariants(assetName, { limit }, description);
      if (!items.length) return { success: true, candidates: [] };
      const candidates = items
        .map(item => ({
          ...this._libraryItemToCandidate(item),
          relevanceScore: this.scoreRelevance(
            { name: item.name, description: item.description || '', tags: item.tags },
            assetName, description, usage
          ),
        }))
        .sort((a, b) => b.relevanceScore - a.relevanceScore);
      return { success: true, candidates };
    } catch (e) {
      console.warn('Library candidate search failed:', e.message);
      return { success: true, candidates: [] };
    }
  }

  /** Search only Sketchfab and return scored candidates. */
  async searchSketchfabCandidates(assetName, options = {}) {
    const { maxResults = 24, description = '', usage = '', category = '', tags: assetTags = '' } = options;
    const sketchfabCategories = category && SKETCHFAB_CATEGORY_MAP[category] ? SKETCHFAB_CATEGORY_MAP[category] : '';
    const sketchfabTags = typeof assetTags === 'string' ? assetTags : Array.isArray(assetTags) ? assetTags.slice(0, 3).join(',') : '';
    const searchOptions = {
      count: maxResults, downloadable: true, sort_by: 'relevance',
      ...(sketchfabCategories && { categories: sketchfabCategories }),
      ...(sketchfabTags && { tags: sketchfabTags }),
    };
    let queryVariants = [];
    try {
      const aiKeywords = await ollamaService.getSketchfabSearchKeywords(assetName, description, usage);
      if (aiKeywords?.length > 0) {
        queryVariants = [...aiKeywords];
        aiKeywords.forEach(k => { const w = `${k.trim()} 3d`; if (!queryVariants.includes(w)) queryVariants.push(w); });
        queryVariants = queryVariants.slice(0, 10);
      }
    } catch (e) {}
    if (!queryVariants.length) queryVariants = this.generateQueryVariants(assetName, description, usage);
    const mainWord = this.getMainSearchWord(assetName);
    if (mainWord) { const t = `${mainWord} 3d`; queryVariants = [mainWord, t, ...queryVariants.filter(q => q !== mainWord && q !== t)]; }

    const allResults = []; const seenUids = new Set();
    for (const query of queryVariants) {
      try {
        const results = await sketchfabService.searchModels(query, searchOptions);
        if (results?.length) {
          for (const m of results) { if (m.isDownloadable !== false && !seenUids.has(m.uid)) { seenUids.add(m.uid); allResults.push(m); } }
          if (allResults.length >= maxResults * 2) break;
        }
      } catch (e) { continue; }
    }
    const candidates = allResults
      .filter(m => m.isDownloadable !== false)
      .map(m => ({ ...m, source: 'sketchfab', relevanceScore: this.scoreRelevance(m, assetName, description, usage) }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, 12);
    return { success: true, candidates };
  }

  /**
   * Download a specific Sketchfab model the user selected from the picker.
   */
  async downloadMeshCandidate(model, assetName) {
    if (model.source === 'library') {
      return await this.downloadFromLibrary(model, assetName);
    }
    const downloadUrl = await sketchfabService.getModelDownloadUrl(model.uid);
    const response = await fetch(downloadUrl);
    if (!response.ok) throw new Error(`Failed to download: ${response.statusText}`);
    const arrayBuffer = await response.arrayBuffer();
    const fileName = `${assetName.replace(/\s+/g, '_')}_${model.uid}.glb`;
    const file = new File([arrayBuffer], fileName, { type: 'model/gltf-binary', lastModified: Date.now() });
    return { success: true, file, fileName, assetType: 'mesh', source: 'sketchfab',
      metadata: { name: model.name, sketchfabUid: model.uid, downloadUrl, relevanceScore: model.relevanceScore } };
  }

  /**
   * Search and download images from Unsplash or Pexels
   */
  async fetchImageAsset(assetName, options = {}) {
    const {
      source = 'unsplash',
      maxResults = 10,
      description = '',
      usage = '',
    } = options;

    // Build enhanced search query
    const searchQuery = this.buildSearchQuery(assetName, description, usage);

    try {
      let imageUrl = null;
      let metadata = {};

      if (source === 'unsplash' && this.unsplashAccessKey) {
        // Try Unsplash first with enhanced query
        const response = await fetch(
          `https://api.unsplash.com/search/photos?query=${encodeURIComponent(searchQuery)}&per_page=${maxResults}&client_id=${this.unsplashAccessKey}`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.results && data.results.length > 0) {
            // Score and select best match
            const scoredImages = data.results
              .map((image) => ({
                ...image,
                relevanceScore: this.scoreRelevance(
                  {
                    name: image.description || image.alt_description || '',
                    description: image.description || '',
                  },
                  assetName,
                  description,
                  usage
                ),
              }))
              .sort((a, b) => b.relevanceScore - a.relevanceScore);

            const image = scoredImages[0];
            imageUrl = image.urls.regular || image.urls.full;
            metadata = {
              name: image.description || image.alt_description || assetName,
              author: image.user?.name,
              authorUrl: image.user?.links?.html,
              license: 'Unsplash License',
              source: 'unsplash',
              unsplashId: image.id,
              relevanceScore: image.relevanceScore,
            };
          }
        }
      }

      // Fallback to Pexels if Unsplash fails or not configured
      if (!imageUrl && this.pexelsApiKey) {
        const response = await fetch(
          `https://api.pexels.com/v1/search?query=${encodeURIComponent(searchQuery)}&per_page=${maxResults}`,
          {
            headers: {
              Authorization: this.pexelsApiKey,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.photos && data.photos.length > 0) {
            // Score and select best match
            const scoredPhotos = data.photos
              .map((photo) => ({
                ...photo,
                relevanceScore: this.scoreRelevance(
                  {
                    name: photo.photographer || '',
                    description: photo.alt || '',
                  },
                  assetName,
                  description,
                  usage
                ),
              }))
              .sort((a, b) => b.relevanceScore - a.relevanceScore);

            const photo = scoredPhotos[0];
            imageUrl = photo.src.large || photo.src.original;
            metadata = {
              name: photo.photographer || assetName,
              author: photo.photographer,
              authorUrl: photo.photographer_url,
              license: 'Pexels License',
              source: 'pexels',
              pexelsId: photo.id,
              relevanceScore: photo.relevanceScore,
            };
          }
        }
      }

      // Fallback to Pixabay if both fail
      if (!imageUrl && this.pixabayApiKey) {
        const response = await fetch(
          `https://pixabay.com/api/?key=${this.pixabayApiKey}&q=${encodeURIComponent(assetName)}&image_type=photo&per_page=${maxResults}`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.hits && data.hits.length > 0) {
            const hit = data.hits[0];
            imageUrl = hit.largeImageURL || hit.webformatURL;
            metadata = {
              name: hit.tags || assetName,
              author: hit.user,
              license: 'Pixabay License',
              source: 'pixabay',
              pixabayId: hit.id,
            };
          }
        }
      }

      if (!imageUrl) {
        throw new Error(`No images found for "${assetName}"`);
      }

      // Download the image
      const imageResponse = await fetch(imageUrl);
      if (!imageResponse.ok) {
        throw new Error(
          `Failed to download image: ${imageResponse.statusText}`
        );
      }

      const blob = await imageResponse.blob();
      const fileName = `${assetName.replaceAll(/\s+/g, '_')}_${Date.now()}.${blob.type.split('/')[1] || 'jpg'}`;
      const file = new File([blob], fileName, {
        type: blob.type || 'image/jpeg',
        lastModified: Date.now(),
      });

      return {
        success: true,
        file,
        fileName,
        assetType: 'image',
        source: metadata.source,
        metadata,
      };
    } catch (error) {
      console.error('Image asset fetch error:', error);
      return {
        success: false,
        error: error.message,
        assetType: 'image',
      };
    }
  }

  /**
   * Search and download videos from Pexels
   */
  async fetchVideoAsset(assetName, options = {}) {
    try {
      if (!this.pexelsApiKey) {
        throw new Error('Pexels API key not configured');
      }

      const { maxResults = 5 } = options;

      const response = await fetch(
        `https://api.pexels.com/videos/search?query=${encodeURIComponent(assetName)}&per_page=${maxResults}`,
        {
          headers: {
            Authorization: this.pexelsApiKey,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Pexels API error: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.videos || data.videos.length === 0) {
        throw new Error(`No videos found for "${assetName}"`);
      }

      // Get the best quality video
      const video = data.videos[0];
      const videoFile =
        video.video_files.find(
          (f) => f.quality === 'hd' || f.quality === 'sd'
        ) || video.video_files[0];

      if (!videoFile || !videoFile.link) {
        throw new Error('No video download link available');
      }

      // Download the video
      const videoResponse = await fetch(videoFile.link);
      if (!videoResponse.ok) {
        throw new Error(
          `Failed to download video: ${videoResponse.statusText}`
        );
      }

      const blob = await videoResponse.blob();
      const fileName = `${assetName.replaceAll(/\s+/g, '_')}_${Date.now()}.mp4`;
      const file = new File([blob], fileName, {
        type: 'video/mp4',
        lastModified: Date.now(),
      });

      return {
        success: true,
        file,
        fileName,
        assetType: 'video',
        source: 'pexels',
        metadata: {
          name: video.url || assetName,
          author: video.user?.name,
          authorUrl: video.user?.url,
          license: 'Pexels License',
          pexelsId: video.id,
          duration: video.duration,
        },
      };
    } catch (error) {
      console.error('Video asset fetch error:', error);
      return {
        success: false,
        error: error.message,
        assetType: 'video',
      };
    }
  }

  /**
   * Search and download audio from Freesound or Pixabay
   */
  async fetchAudioAsset(assetName, options = {}) {
    try {
      const { maxResults = 5, source = 'freesound' } = options;
      let audioUrl = null;
      let metadata = {};

      // Try Freesound first
      if (source === 'freesound' && this.freesoundApiKey) {
        const response = await fetch(
          `https://freesound.org/apiv2/search/text/?query=${encodeURIComponent(assetName)}&fields=id,name,previews,username,license&page_size=${maxResults}`,
          {
            headers: {
              Authorization: `Token ${this.freesoundApiKey}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.results && data.results.length > 0) {
            const sound = data.results[0];
            audioUrl =
              sound.previews?.['preview-hq-mp3'] ||
              sound.previews?.['preview-lq-mp3'];
            metadata = {
              name: sound.name || assetName,
              author: sound.username,
              license: sound.license,
              source: 'freesound',
              freesoundId: sound.id,
            };
          }
        }
      }

      // Fallback to Pixabay
      if (!audioUrl && this.pixabayApiKey) {
        const response = await fetch(
          `https://pixabay.com/api/?key=${this.pixabayApiKey}&q=${encodeURIComponent(assetName)}&audio_type=all&per_page=${maxResults}`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.hits && data.hits.length > 0) {
            const hit = data.hits[0];
            audioUrl = hit.url || hit.preview_url;
            metadata = {
              name: hit.tags || assetName,
              author: hit.user,
              license: 'Pixabay License',
              source: 'pixabay',
              pixabayId: hit.id,
            };
          }
        }
      }

      if (!audioUrl) {
        throw new Error(`No audio files found for "${assetName}"`);
      }

      // Download the audio
      const audioResponse = await fetch(audioUrl);
      if (!audioResponse.ok) {
        throw new Error(
          `Failed to download audio: ${audioResponse.statusText}`
        );
      }

      const blob = await audioResponse.blob();
      const fileName = `${assetName.replaceAll(/\s+/g, '_')}_${Date.now()}.mp3`;
      const file = new File([blob], fileName, {
        type: 'audio/mpeg',
        lastModified: Date.now(),
      });

      return {
        success: true,
        file,
        fileName,
        assetType: 'audio',
        source: metadata.source,
        metadata,
      };
    } catch (error) {
      console.error('Audio asset fetch error:', error);
      return {
        success: false,
        error: error.message,
        assetType: 'audio',
      };
    }
  }

  /**
   * Add asset to Redux state (similar to handleUpload functions)
   * This makes the asset available for publish without needing scene/Three.js context
   * The Publish function checks for objPath instanceof File to upload files
   */
  addAssetToReduxState(file, assetType, assetName, metadata = {}) {
    try {
      const tempId = `temp-${Date.now()}-${file.name}`;
      const objectURL = URL.createObjectURL(file);

      // Create a minimal object representation for Redux
      // This mimics what handleUpload functions create
      // The key is objPath = file (File object) so Publish function can find and upload it
      // IMPORTANT: Must include position, rotation, scale for Publish function to process correctly
      const assetObject = {
        name: tempId,
        _id: tempId,
        url: tempId,
        objPath: file, // Store File object - this is what Publish function checks for upload
        originalFile: file,
        file_name: file.name,
        temp_file_name: tempId,
        deleteType: assetType,
        asset_file_name: assetName || file.name,
        // Position, rotation, scale - required for Publish function
        // Default to origin/zero rotation/unit scale if not provided
        position: metadata.position || { x: 0, y: 0, z: 0 },
        rotation: metadata.rotation || { x: 0, y: 0, z: 0 },
        scale: metadata.scale || { x: 1, y: 1, z: 1 },
        // Store metadata
        userData: {
          material_settings: {
            albedo: '#00FFFFFF',
            metalicType: false,
            emissionType: false,
            added: true,
          },
          ...metadata,
          type: 'sketchfab',
        },
      };


      // Add type-specific properties based on asset type
      if (assetType === 'mesh') {
        assetObject.objType = '3d';
        assetObject.glb_type = 'normal';
        store.dispatch(addModel({ url: objectURL, name: file.name }));
        store.dispatch(addSceneMesh(assetObject));
      } else if (assetType === 'image') {
        store.dispatch(addImage({ url: objectURL, name: file.name }));
        store.dispatch(addSceneImage(assetObject));
      } else if (assetType === 'video') {
        assetObject.userData.video_settings = metadata.video_settings || {
          video_source: objectURL,
        };
        store.dispatch(addVideo({ url: objectURL, name: file.name }));
        store.dispatch(addSceneVideo(assetObject));
      } else if (assetType === 'audio') {
        assetObject.userData.audio_settings = metadata.audio_settings || {
          audio_source: objectURL,
        };
        store.dispatch(addAudio({ url: objectURL, name: file.name }));
        store.dispatch(addSceneAudio(assetObject));
      }

      // Add to assets_data (this is what Publish function reads from Redux)
      store.dispatch(add_new_assets({ type: assetType, data: assetObject }));

      return {
        success: true,
        tempId,
        assetObject,
        objectURL,
      };
    } catch (error) {
      console.error('Error adding asset to Redux state:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Automatically fetch and upload assets based on training analysis
   * This is the main function to call from the training module
   */
  async autoFetchAssets(projectId, neededAssets, options = {}) {
    const { onProgress = null, onAssetFetched = null } = options;

    const results = {
      success: [],
      failed: [],
      skipped: [],
    };

    const assetsToFetch = neededAssets
      .filter((asset) => asset.status === 'needed' || asset.status === undefined)
      .slice(0, MAX_ASSETS_PER_AUTOFETCH);

    if (assetsToFetch.length === 0) {
      return {
        success: true,
        message: 'No assets need to be fetched',
        results,
      };
    }

    if (neededAssets.filter((a) => a.status === 'needed' || !a.status).length > MAX_ASSETS_PER_AUTOFETCH) {
      toast(`Limiting to first ${MAX_ASSETS_PER_AUTOFETCH} assets per run`, { id: 'auto-fetch-limit', duration: 3000 });
    }

    toast.loading(
      `Fetching ${assetsToFetch.length} asset(s) from the internet...`,
      {
        id: 'auto-fetch',
      }
    );

    for (let i = 0; i < assetsToFetch.length; i++) {
      const asset = assetsToFetch[i];
      const assetType = asset.assetType || asset.type || 'mesh';
      const assetName = asset.name;

      try {
        // Report progress
        if (onProgress) {
          onProgress({
            current: i + 1,
            total: assetsToFetch.length,
            asset: assetName,
            status: 'fetching',
          });
        }

        // Fetch asset based on type; pass description, usage, category for more exact matches (e.g. Sketchfab)
        let fetchResult;
        const assetOptions = {
          description: asset.description || '',
          usage: asset.usage || '',
          ...(asset.category && { category: asset.category }),
          ...(asset.placement_context?.tags && { tags: Array.isArray(asset.placement_context.tags) ? asset.placement_context.tags.join(',') : asset.placement_context.tags }),
        };

        switch (assetType) {
          case 'mesh':
            fetchResult = await this._fetchWithRetry(() => this.fetchMeshAsset(assetName, assetOptions));
            break;
          case 'image':
            fetchResult = await this._fetchWithRetry(() => this.fetchImageAsset(assetName, assetOptions));
            break;
          case 'video':
            fetchResult = await this._fetchWithRetry(() => this.fetchVideoAsset(assetName, assetOptions));
            break;
          case 'audio':
            fetchResult = await this._fetchWithRetry(() => this.fetchAudioAsset(assetName, assetOptions));
            break;
          default:
            throw new Error(`Unsupported asset type: ${assetType}`);
        }

        if (!fetchResult.success) {
          results.failed.push({
            asset,
            error: fetchResult.error,
          });
          continue;
        }

        // Upload to project
        if (onProgress) {
          onProgress({
            current: i + 1,
            total: assetsToFetch.length,
            asset: assetName,
            status: 'uploading',
          });
        }

        // Prepare metadata with transform values if available
        const metadata = {
          ...fetchResult.metadata,
          description: asset.description || '',
          usage: asset.usage || '',
        };

        // Include transform values if available from LLM analysis
        if (asset.transform) {
          // Convert rotation from degrees to radians for Three.js compatibility
          metadata.transform = {
            position: asset.transform.position || { x: 0, y: 0, z: 0 },
            rotation: {
              x: (asset.transform.rotation?.x || 0) * (Math.PI / 180),
              y: (asset.transform.rotation?.y || 0) * (Math.PI / 180),
              z: (asset.transform.rotation?.z || 0) * (Math.PI / 180),
            },
            scale: asset.transform.scale || { x: 1, y: 1, z: 1 },
          };
        }

        const uploadResult = await this.uploadAssetToProject(
          projectId,
          fetchResult.file,
          fetchResult.assetType,
          assetName,
          metadata
        );

        if (uploadResult.success) {
          results.success.push({
            asset,
            uploadedAsset: uploadResult.asset,
            assetId: uploadResult.assetId,
            source: fetchResult.source,
          });

          // Callback for successful fetch
          if (onAssetFetched) {
            onAssetFetched({
              originalAsset: asset,
              uploadedAsset: uploadResult.asset,
              assetId: uploadResult.assetId,
              source: fetchResult.source,
            });
          }
        } else {
          results.failed.push({
            asset,
            error: 'Upload failed',
          });
        }
      } catch (error) {
        console.error(`Error fetching asset ${assetName}:`, error);
        results.failed.push({
          asset,
          error: error.message,
        });
      }
    }

    // Update toast
    const successCount = results.success.length;
    const failedCount = results.failed.length;

    if (failedCount === 0) {
      toast.success(
        `Successfully fetched and uploaded ${successCount} asset(s)!`,
        { id: 'auto-fetch' }
      );
    } else {
      toast.error(
        `Fetched ${successCount} asset(s), ${failedCount} failed. Check console for details.`,
        { id: 'auto-fetch', duration: 5000 }
      );
    }

    return {
      success: failedCount === 0,
      results,
      summary: {
        total: assetsToFetch.length,
        success: successCount,
        failed: failedCount,
        skipped: results.skipped.length,
      },
    };
  }

  /**
   * Search for assets without downloading (for preview)
   */
  async searchAssets(assetName, assetType, options = {}) {
    const { maxResults = 10 } = options;

    try {
      switch (assetType) {
        case 'mesh':
          return await sketchfabService.searchModels(assetName, {
            count: maxResults,
            downloadable: true,
          });
        case 'image':
          // Search multiple sources
          const results = [];
          // Add Unsplash, Pexels, Pixabay results
          return results;
        default:
          return [];
      }
    } catch (error) {
      console.error('Asset search error:', error);
      return [];
    }
  }
}

// Export singleton instance
export const assetAutomationService = new AssetAutomationService();
